import React, { useCallback } from "react";
import * as styles from "./SearchResults.module.scss";
import clsx from "clsx";
import { useHits } from "react-instantsearch";
import AlgoliaPagination from "components/Algolia/AlgoliaPagination";
import { AlgoliaCruiseHit } from "types/algolia";
import { CruiseCardModel } from "components/CruiseCard/types";
import CruiseCardResult, { CruiseCardResultProps } from "../CruiseCardResult";
import { trackAlgoliaResultClick } from "components/Algolia/algoliaAnalytics";
import { useDrawer } from "@silversea-ssc/ui-components-react";

const CruiseDrawer = React.lazy(
  () => import("components/CruiseDrawer/CruiseDrawer")
);

interface Props {
  onPaginationChanged: () => void;
  hitComponentProps: {
    showDestination?: boolean;
    promosBadgeContent: CruiseCardResultProps["promosBadgeContent"];
    shipsContent: CruiseCardResultProps["shipsContent"];
    backgroundColor: "white" | "neutral01";
  };
}

type UseDrawerState =
  | { cruise: CruiseCardModel; hit: AlgoliaCruiseHit }
  | undefined;

const SearchResults: React.FC<Props> = ({
  onPaginationChanged,
  hitComponentProps = {} as Props["hitComponentProps"]
}) => {
  const { items, sendEvent, results } = useHits<AlgoliaCruiseHit>();
  const {
    closeDrawer,
    drawerData,
    isDrawerMountable,
    isDrowerOpen,
    openDrawer
  } = useDrawer<UseDrawerState>(undefined);

  const onCruiseClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      cruiseCard: CruiseCardModel,
      hit: AlgoliaCruiseHit
    ) => {
      e.preventDefault();
      openDrawer({ cruise: cruiseCard, hit: hit });
    },
    [openDrawer]
  );

  const onCloseCruiseDrawer = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const onViewItineraryClick = useCallback(
    (hit: AlgoliaCruiseHit) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      trackAlgoliaResultClick(sendEvent, hit, "Drawer view itinerary clicked");
    },
    [sendEvent]
  );

  return (
    <div>
      <ul className={styles.searchResults}>
        {items.map((hit) => (
          <li key={hit.objectID} className={styles.item}>
            <CruiseCardResult
              hit={hit}
              refinements={results?.getRefinements()}
              filters={results?._state.filters}
              {...hitComponentProps}
              onClick={onCruiseClick}
              algoliaSendEvent={sendEvent}
            />
          </li>
        ))}
      </ul>
      <div className={clsx(styles.pagination)}>
        <AlgoliaPagination onClick={onPaginationChanged} />
      </div>
      {isDrawerMountable && drawerData && (
        <React.Suspense>
          <CruiseDrawer
            cruiseCard={drawerData?.cruise}
            isOpen={isDrowerOpen}
            onRequestClose={onCloseCruiseDrawer}
            queryID={drawerData.hit.__queryID}
            onViewItineraryClick={
              drawerData.hit ? onViewItineraryClick(drawerData.hit) : undefined
            }
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default SearchResults;
